/**
 * Definizione dell' interfaccia e della classe CausaleWFRecord, la struttura dati che rappresenta la causale di workflow.
 * ATTENZIONE: si tratta della causale che compone l'elenco di selezione della causale di workflow nell'ambito della richiesta di assenze/presenze.
 * La causale è contraddistinta da un codice e una descrizione (l'unicità è data da idTabella e codice).
 * Una serie di proprietà determinano il comportamento della causale stessa in termini di fruibilità (ambitoTemporale, controlloSuPrevisto), di validazione in fase d'inserimento della quantità, etc.
 */
import {URIAndTrace} from "@commons/mapping/URIAndTrace";
import {StatoRichiesta} from "@commons/mapping/Workflow";
import {GiustificativoAggancioRecord} from "@commons/mapping/GiustificativoAggancioRecord";
import {GiustificativoRecord} from "@commons/mapping/GiustificativoRecord";
import {FPNetDate} from "@commons/types";
import {DateTime} from "luxon";
import {SaldoICRecord} from "@commons/mapping/SaldoICRecord";
import {datesDifference} from "@commons/utils/util";
import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import {LavoratorePresenzeRecord} from "@commons/mapping/LavoratorePresenzeRecord";
import {LavoratoreBase} from "@commons/mapping/LavoratoreBase";

export type TipoCausaleWF = 'ORE_MANCANTI' | 'ORE_ECCEDENTI' | 'QUANTITA' | 'MANUTENZIONE_TIMBRATURE';

export interface CausaleWFRecord extends URIAndTrace {
    idDominio: number,
    idTabella: number,
    codice: string,
    descrizione: string,

    ambitoTemporale: 'SEMPRE' | 'PREVENTIVO' | 'CONSUNTIVO' | 'AUTOMATICO',
    tipoCausale: TipoCausaleWF,
    controlloSuPrevisto: 'QUALSIASI' | 'CON_PREVISTO' | 'SENZA_PREVISTO',

    azioneMancante: 'COME_WF' | 'COME_WF_ANOMALIA' | 'PREVISTO' | 'MANCANTE' | 'PREVISTO_WF0' | 'MANCANTE_WF0' | 'PREVISTO_WFM' | 'MANCANTE_WFM' | 'COME_DEFAULT',
    azioneEccedente: 'COME_WF' | 'ECCEDENTE' | 'ECCEDENTE_WF0' | 'ECCEDENTE_WFM' | 'COME_DEFAULT',
    azioneQuantitaNonCoincidenti: 'SET_OK' | 'SET_WARNING' | 'UNSET_ERROR' | 'SET_QUANTITA' | 'SET_DEFAULT' | 'SET_DEFAULT_E_WARNING',

    quantita: number, // Quantità di default
    opzioni: number,

    colore: string, // '#ffccdd'
    coloreAllert: string,
    coloreWarning: string,

    suggerimentoNote: string,
    statoIniziale: StatoRichiesta,

    agganci: GiustificativoAggancioRecord[], // GiustificativoAggancioRecord[]
    codIstitutoContrattuale: string, // Codice istituto contrattuale vincolato automaticamente dai giustificativi associati alla causale e all'istituto stesso!

    valMin: number; // Valore minimo espresso in hh.cc se la causale riguarda una quantità oraria
    valMax: number; // Valore massimo espresso in hh.cc se la causale riguarda una quantità oraria
    decimaleMin: number; // in minuti se la causale riguarda una quantità oraria, altrimenti in centesimi
    decimaleMultipliDi: number; // in minuti se la causale riguarda una quantità oraria, altrimenti in centesimi
}

export class CausaleWFRecord {
    static isMancante = (causale: Pick<CausaleWFRecord, 'tipoCausale'>) => causale && causale.tipoCausale === 'ORE_MANCANTI';
    static isQuantitaOraria = function (causale: CausaleWFRecord) {
        return causale.tipoCausale === 'ORE_MANCANTI' || causale.tipoCausale === 'ORE_ECCEDENTI';
    };
    static isOrario = (causale: CausaleWFRecord) => {
        return CausaleWFRecord.isQuantitaOraria(causale);
    };
    static isQuantita = (causale: CausaleWFRecord) => causale && causale.tipoCausale === 'QUANTITA';
    static permettiModificaQuantita = (causale: Pick<CausaleWFRecord, 'opzioni'> | null) => causale && causale.opzioni & 0x01;
    static permettiModificaGiustificativo = (causale: Pick<CausaleWFRecord, 'opzioni'> | null) => causale && causale.opzioni & 0x02;
    static notaObbligRichiedente = (causale: Pick<CausaleWFRecord, 'opzioni'> | null) => causale && causale.opzioni & 0x04;
    static notaObbligApprovatore = (causale: Pick<CausaleWFRecord, 'opzioni'> | null) => causale && causale.opzioni & 0x08;
    static notificaGiaEvasi = (causale: Pick<CausaleWFRecord, 'opzioni'> | null) => causale && causale.opzioni & 0x10;
    static isIntervalloFacoltativo = (causale: Pick<CausaleWFRecord, 'opzioni'> | null) => causale ? ((causale.opzioni & 0x20) === 0x20) : false;
    static soloSingolaGiornata = (causale: Pick<CausaleWFRecord, 'opzioni'> | null) => causale && causale.opzioni & 0x40;
    static isAssente = (causale: Pick<CausaleWFRecord, 'opzioni'> | null) => causale && causale.opzioni & 0x80;
    static isSoloPerInteraGiornata = (causale: Pick<CausaleWFRecord, 'opzioni'> | null) => causale ? ((causale.opzioni & 0x100) === 0x100) : false;
    static isSoloPerFrazioneGiornata = (causale: Pick<CausaleWFRecord, 'opzioni'> | null) => {
        //console.log('solo per frazione gg ', causale ? ((causale.opzioni & 0x200) === 0x200) : false)
        return causale ? ((causale.opzioni & 0x200) === 0x200) : false
    };

    static hasRange = (causale: CausaleWFRecord) => {
        return causale ? !((causale.valMin === causale.valMax) && causale.valMax === 0) : false;
    };

    static hasFixedVal = (causale: CausaleWFRecord) => {
        if (causale) {
            if (!CausaleWFRecord.isOrario(causale)) {
                if (CausaleWFRecord.hasRange(causale)) {
                    if (causale.valMin === causale.valMax) {
                        return true;
                    }
                }
            }
        }
        return false;
    };

    /**
     *
     * @param causale
     * @param nGiu quantità espressa in centesimi!
     * @param _nPrevisto previsto espresso in minuti!
     * @returns {boolean}
     */
    static isRangeValid = (causale: CausaleWFRecord, nGiu: number, _nPrevisto?: number): boolean => {
        if (!causale)
            return true;
        if (!CausaleWFRecord.hasRange(causale)) {
            return true;
        } else {
            const isOrario = CausaleWFRecord.isOrario(causale);
            const divisore = isOrario ? 60 : 1;

            const nGiuMinuti = nGiu * divisore;
            let valid;

            // verifico range valore (parte intera e decimale), tutto in centesimi
            if (causale.valMax === 0) {
                valid = (nGiu >= causale.valMin);
            } else {
                valid = (nGiu >= causale.valMin) && (nGiu <= causale.valMax);
            }

            // Verifico se è ammesso un valore a zero per giornate senza previsto (es. malattia)
            /*if (!valid && causale.consenti0sePrev0) {
                valid = ((nGiu === 0) && (nPrevisto === 0));
            }*/

            // verifico parte decimale
            if (valid) {
                let decimali;
                if (isOrario) {
                    decimali = nGiuMinuti % 60; // se giustificativo orario, la quantità frazionaria viene calcolata in minuti!
                } else {
                    // https://stackoverflow.com/questions/4512306/get-decimal-portion-of-a-number-with-javascript
                    const resto = Number.parseFloat((nGiu % 1).toFixed(2));
                    const interi = (nGiu - resto); // parte intera in ore (se giu orario)
                    decimali = nGiu - interi;
                    decimali = decimali * 100;
                    decimali = decimali - decimali % 1;
                }
                if (decimali > 0) { // se ho una parte frazionaria...
                    const minimo = causale.decimaleMin; // N.B. se giustificativo orario la quantità minima è gia espressa in minuti
                    valid = (minimo === 0 || decimali >= minimo);
                    if (valid) {
                        if (causale.decimaleMultipliDi > 0) {
                            const multipli = causale.decimaleMultipliDi; // N.B. se giustificativo orario la quantità multipliDi è gia espressa in minuti
                            valid = (decimali % multipli) === 0;
                        }
                    }
                }
            }
            return valid;
        }
    };

    /**
     * routine di validazione del campo note per richiedente e approvatore
     * @param causale
     * @param soggetto
     * @param v
     */
    static isNoteValid = (causale: CausaleWFRecord | null, soggetto: 'richiedente' | 'approvatore', v: string): boolean => {
        if (causale) {
            switch (soggetto) {
                case 'richiedente':
                    if (CausaleWFRecord.notaObbligRichiedente(causale))
                        return (!!v.trim());
                    break;

                case 'approvatore':
                    if (CausaleWFRecord.notaObbligApprovatore(causale))
                        return (!!v.trim());
                    break;
                default:
                    break;
            }
            return true;
        } else return true;
    }

    static getRangeText2021 = (causale: CausaleWFRecord): string => {
        if (!causale)
            return '';
        if (CausaleWFRecord.hasRange(causale)) {
            let str;
            if (causale.valMax === 0) {
                str = 'Valore min.: ' + causale.valMin;
            } else {
                str = 'Valore min.: ' + causale.valMin + ', valore max: ' + causale.valMax;
            }

            if ((causale.decimaleMin > 0) || (causale.decimaleMultipliDi > 0)) {
                str = str + ' - parte frazionaria: min. ' + causale.decimaleMin + (CausaleWFRecord.isOrario(causale) ? ' minuti' : ' cent.');
                if (causale.decimaleMultipliDi > 0) {
                    str = str + ' e multipli di ' + causale.decimaleMultipliDi + (CausaleWFRecord.isOrario(causale) ? ' minuti' : ' cent.');
                }
            }

            return str;
        }
        return 'nessun range...';
    };

    /**
     *
     * @param causale la causale da valutare
     * @param giustificativi elenco dei giustificativi gia filtrati sugli status del lavoratore
     */
    static filterHasStatus = (causale: CausaleWFRecord, giustificativi: GiustificativoRecord[]) => {
        if ((causale.codice && causale.codice.length > 0) && (!causale.agganci || causale.agganci.length === 0)) {
            console.warn(`codice: ${causale.codice} - Causale senza agganci vs giustificativi!`);
            return false;
        } else {
            for (const {codGiu} of causale.agganci) {
                const giustificativo = giustificativi.find(giu => giu.codGiu === codGiu)
                if (giustificativo) {
                    return true;
                }
            }
        }
        return false;
    }
    static filterAmbitoTemporale = (causale: CausaleWFRecord, giornoDt: FPNetDate) => {
        if (causale.ambitoTemporale === 'SEMPRE')
            return true;
        if (causale.ambitoTemporale === 'AUTOMATICO')
            return false;
        if (giornoDt) {
            if (causale.ambitoTemporale === 'PREVENTIVO')
                return DateTime.now().startOf('day') <= DateTime.fromISO(giornoDt).startOf('day');
            if (causale.ambitoTemporale === 'CONSUNTIVO')
                return DateTime.now().startOf('day') > DateTime.fromISO(giornoDt).endOf('day');
        } else return false;
    }

    static filterHasPrevisto = (causale: CausaleWFRecord, previsto: number | null) => {
        if (previsto === null) // Se non posso valutare il previsto allora causale valida!
            return true;

        switch (causale.controlloSuPrevisto) {
            case "QUALSIASI":
                return true;
            case "CON_PREVISTO":
                if (previsto > 0)
                    return true
                break;
            case "SENZA_PREVISTO":
                if (previsto === 0)
                    return true
                break;
            default:
                return false;
        }
    }

    static filterCheckIstitutoContrattuale = (causale: CausaleWFRecord, lavoratore: LavoratorePresenzeRecord | LavoratoreBase) => {
        if (!causale.codIstitutoContrattuale || causale.codIstitutoContrattuale === '') {
            return true;
        } else {
            const saldiIC = lavoratore && ('ic' in lavoratore) && lavoratore.ic?.saldiIC;
            if (saldiIC) {
                const saldo = SaldoICRecord.cercaSaldo(causale.codIstitutoContrattuale, saldiIC)
                if (saldo && saldo.contrattuale > 0) {
                    return true;
                }
            } else return false;
        }
    }

    static checkPiuGiorni = (causale: CausaleWFRecord | null, ggInizio: FPNetDate, ggFine: FPNetDate) => {
        if (!causale)
            return true;
        if (CausaleWFRecord.soloSingolaGiornata(causale)) {
            return !datesDifference(ggInizio, ggFine);
            //!true - stesso giorno
            //!false - giorni diversi
        } else {
            return false;
        }
    }

    // Restituisce true se la causale è compliance specularmente al giustificativo agganciato
    // N.B. basta un giustificativo non compliance e anche la causale risulterà non compliance
    static checkDayCompliance = (causale: CausaleWFRecord, elencoGiustificativi: GiustificativoRecord[] | undefined, day: PresenzeGiornataPlusRecord): boolean => {
        if (causale.agganci && elencoGiustificativi) {
            for (const {codGiu} of causale.agganci) {
                const giustificativo = elencoGiustificativi.find(giu => giu.codGiu === codGiu);
                if (giustificativo && !GiustificativoRecord.isDayCompliance(giustificativo, day)) {
                    return false;
                }
            }
            return true;
        } else {
            return true;
        }
    }

    static verificaMonteOreMancante = (that: CausaleWFRecord | null,
                                       watchGIntera: boolean,
                                       isSameDay: boolean,
                                       quantitaRichiesta: number,
                                       {hhMancanti, hhPreviste}: { hhMancanti: number, hhPreviste: number | null }): {
        isValidQuantita: boolean,
        isValidQuantitaParziale: boolean
    } => {
        let isValidQuantita = true;
        let isValidQuantitaParziale = true;
        if (hhPreviste !== null && isSameDay) {  // Eseguo controlli sulla quantità SOLO SE il periodo della richiesta riguarda 1 SOLO GIORNO...

            let mancanteGiustificato = hhMancanti || 0;

            if (that?.tipoCausale === 'ORE_MANCANTI') {
                if (watchGIntera)
                    mancanteGiustificato += hhPreviste;
                else
                    mancanteGiustificato += (quantitaRichiesta * 60);

                isValidQuantita = hhPreviste >= mancanteGiustificato;

                if (isValidQuantita && CausaleWFRecord.isSoloPerFrazioneGiornata(that)) {
                    isValidQuantitaParziale = (quantitaRichiesta * 60) < hhPreviste;
                }
            }
        } else {
            if (hhPreviste === null) {
                // Se validazione dalla pagina del monitor non posso valutare il previsto
                //console.warn('Previsto non disponibile, quindi NON verifico la quadratura giornaliera!!!');
            }
            if (!isSameDay) {
                // Se il periodo riguarda più giorni evito di verificare la quadratura giornaliera...
                //console.warn('Il periodo riguarda più giorni, quindi NON verifico la quadratura giornaliera!!!');
            }
        }
        return {isValidQuantita, isValidQuantitaParziale};
    }

    static getDescrizione = function (causale: CausaleWFRecord | undefined) {
        if (causale)
            return causale.descrizione + ' [' + causale.codice + ']';
        else return '???'
    };

}